import { BrokerInvoiceStatus } from '@numeo/types-core'
import type { TmsInvoiceCreateRow, TmsInvoiceRow } from '@numeo/types-main-web-app'
import invoiceApi from '@/services/invoiceApi'
import { log } from '@/utils/logger'
import { approveWrite as approveWriteCard } from '../../api/writeCardApi'
import type { ChatNoteAction } from '../../types'
import { formatUsd } from '../results/format'
import type { ApproveWrite } from './sendBatch'
import { eligibleOf, knownTotalOf, knownTotalWords, rowKeyOf } from './invoicingRows'
import { FACTORING_BATCH_MIN } from '../results/refusalWords'

/** The Invoices page's own "Group into batch" (`POST /accountant/broker-invoice/batch/group`): one batch of any size, nothing sent. */
type GroupIntoBatch = typeof invoiceApi.groupInvoicesIntoBatch

/** What the batch did: its invoices and the page's words for it; a prepared one also its name, count and total. */
interface BatchOutcome {
    invoiceNumbers: string[]
    /** The invoices this run created before it stopped (they stay Invoiced): a batch that was not prepared names them. */
    createdNumbers?: string[]
    prepared: boolean
    message: string
    /** The batch the page made, when prepared. */
    batchName?: string
    /** The factor's display name, when the answer names one. */
    provider?: string
    invoiceCount?: number
    totalUsd?: number
    /** Invoices in the batch with no amount: the total leaves them out and says so. */
    missingAmounts?: number
    /** Whether the batch may be submitted from the chat (`tms_factoring.submit_batch`), else from the Invoices page. */
    submitInChat?: boolean
}

/** What a factoring batch could take from these rows: the eligible ones, and whether they are enough for a batch. */
interface FactoringEligibility {
    eligible: TmsInvoiceRow[]
    /** At least the minimum a new batch takes (`FACTORING_BATCH_MIN`). */
    batchable: boolean
}

/**
 * The one eligibility rule of every factoring offer and of the batch drawer: the rows that can be batched (`eligibleOf`: ready loads and
 * drafts, never a sent, batched or issue-blocked one), and whether there are enough of them for a batch.
 */
export const factoringEligibilityOf = (rows: TmsInvoiceRow[]): FactoringEligibility => {
    const eligible = eligibleOf(rows)
    return { eligible, batchable: eligible.length >= FACTORING_BATCH_MIN }
}

/**
 * Prepares the eligible rows as ONE factoring batch, as the Invoices page's "Group into batch" does: any number of invoices, nothing sent
 * to the factor (submitting stays a separate step). A load with no invoice gets its single-load invoice first (the drawer's button is the
 * approval, `approvedInDrawer`); one that cannot be invoiced stops the batch, so it never holds less than the drawer listed. A failed
 * call is reported and not retried.
 */
export async function prepareFactoringBatch(
    tabId: string,
    rows: TmsInvoiceRow[],
    approveWrite: ApproveWrite = approveWriteCard,
    group: GroupIntoBatch = invoiceApi.groupInvoicesIntoBatch
): Promise<BatchOutcome> {
    const ids: string[] = []
    const numbers: string[] = []
    // Created by this run, one by one: a later stop leaves them Invoiced, so a batch that was not prepared names them.
    const created: string[] = []
    const stopped = (message: string): BatchOutcome => ({ invoiceNumbers: numbers, createdNumbers: created, prepared: false, message })
    try {
        for (const row of rows) {
            if (row.invoice_id) {
                ids.push(row.invoice_id)
                if (row.invoice_number) numbers.push(row.invoice_number)
                continue
            }
            // Only the load's own number creates its invoice: any other key could name another load.
            const loadNumber = row.load_numbers[0]
            if (!loadNumber) return stopped('A row has no load number, so its invoice cannot be created here. Create it on the Invoices page.')
            const answer = await approveWrite(tabId, 'tms_invoices.create', { load_numbers: [loadNumber] }, { approvedInDrawer: true })
            const line = answer.structured.rows?.find((r) => r.load_number === row.load_numbers[0]) as TmsInvoiceCreateRow | undefined
            if (!line?.invoice_id) return stopped(line?.message ?? answer.text)
            ids.push(line.invoice_id)
            if (line.invoice_number) {
                numbers.push(line.invoice_number)
                created.push(line.invoice_number)
            }
        }
        const answer = await group({ invoiceIds: ids })
        if (!answer.success || !answer.data) return stopped(answer.message || 'The factoring batch was not prepared.')
        const { total, missing } = knownTotalOf(rows)
        return {
            invoiceNumbers: numbers,
            createdNumbers: created,
            prepared: true,
            message: '',
            batchName: answer.data.batchName,
            invoiceCount: answer.data.invoiceCount,
            totalUsd: Math.round(total * 100) / 100,
            ...(missing ? { missingAmounts: missing } : {}),
        }
    } catch (error) {
        log.e('[invoicing] factoring batch not prepared', { invoices: rows.length, error })
        return stopped('Not prepared. The request failed, so nothing was retried.')
    }
}

/** What a batch that was not prepared left behind: the invoices it created before it stopped, which stay Invoiced; empty when none. */
export const createdBeforeStopWords = (outcome: BatchOutcome): string => {
    const created = outcome.createdNumbers ?? []
    if (!created.length) return ''
    return `${created.length === 1 ? 'Invoice' : 'Invoices'} ${created.join(', ')} ${created.length === 1 ? 'was' : 'were'} created before it stopped and ${created.length === 1 ? 'stays' : 'stay'} Invoiced.`
}

/**
 * The agent message a prepared batch adds to the chat (v17): "Prepared BATCH-… for RTS Pro: 2 invoices, $1,800.00. It is not sent to the
 * factor yet." With submitting from the chat on, its next step asks the agent to submit the batch: that is the user's chat message, and
 * `tms_factoring.submit_batch` asks its own approval as always (it is never answered from here). Off, it says where to submit it.
 */
export const preparedNoteOf = (outcome: BatchOutcome, fallbackProvider: string): [string, ChatNoteAction?] => {
    const provider = outcome.provider || fallbackProvider || 'the factor'
    const count = outcome.invoiceCount ?? outcome.invoiceNumbers.length
    const amount = knownTotalWords({ total: outcome.totalUsd ?? 0, missing: outcome.missingAmounts ?? 0 }, formatUsd)
    const text = `Prepared ${outcome.batchName} for ${provider}: ${count} invoice${count === 1 ? '' : 's'}, ${amount}. It is not sent to the factor yet.`
    if (!outcome.submitInChat) return [`${text} Open Accounting → Factoring to submit it.`]
    return [text, { label: `Submit to ${provider}`, message: `Submit factoring batch ${outcome.batchName} to ${provider}.` }]
}

/** What the factoring offer stands for: the rows a batch would take (the table's, then the company's others) and their whole count. */
interface FactoringOffer {
    rows: TmsInvoiceRow[]
    count: number
}

/**
 * The factoring offer over the company, not only the table (S17): the table's eligible rows, then each batchable invoice the server read
 * (`company`) that the table does not list and that is still eligible as the store shows it, and the invoices past the read's 50 by
 * count. With no company read yet, the table's rows alone.
 */
export const factoringOfferOf = (tableEligible: TmsInvoiceRow[], company: { rows: TmsInvoiceRow[]; total: number | null }): FactoringOffer => {
    if (company.total === null) return { rows: tableEligible, count: tableEligible.length }
    const listed = new Set(tableEligible.flatMap((row) => [rowKeyOf(row), ...(row.invoice_number ? [`#${row.invoice_number}`] : [])]))
    const others = eligibleOf(company.rows).filter((row) => !listed.has(rowKeyOf(row)) && !(row.invoice_number && listed.has(`#${row.invoice_number}`)))
    const rows = [...tableEligible, ...others]
    // An invoice on the table that the read's first 50 do not hold is one of the invoices past the read: count it once, as a row.
    const read = new Set(company.rows.flatMap((row) => [rowKeyOf(row), ...(row.invoice_number ? [`#${row.invoice_number}`] : [])]))
    const pastReadOnTable = tableEligible.filter(
        (row) =>
            row.invoice_id &&
            row.status_stored === BrokerInvoiceStatus.INVOICED &&
            !read.has(rowKeyOf(row)) &&
            !(row.invoice_number && read.has(`#${row.invoice_number}`))
    ).length
    return { rows, count: rows.length + Math.max(0, company.total - company.rows.length - pastReadOnTable) }
}
